import React from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { OptionalService } from '../../types';
import { GetPAXTotalsResponse, separadorDeMiles } from '../../helpers';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Link, Switch } from '@mui/material';
import { renderDescription } from '../../helpers';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		media: {
			height: 0,
			paddingTop: '45%',
			backgroundColor: '#f2eef6',
		},
		title: {
			color: theme.palette.primary.main,
			fontWeight: 800,
			fontSize: '1rem',
			lineHeight: 1.15,
		},
		prices: {
			display: 'flex',
			alignItems: 'left',
			flexDirection: 'column',
			paddingLeft: theme.spacing(2),
			paddingRight: theme.spacing(2),
			paddingBottom: theme.spacing(1),
			placeContent: 'space-evenly',
		},
		pricesText: {
			color: theme.palette.secondary.main,
			fontWeight: 'bold',
		},
		flex: {
			display: 'flex',
		},
		priceRow: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
		},
	}),
);
interface Props {
	optionalService: OptionalService;
	paxTotals: GetPAXTotalsResponse;
	selectable?: boolean;
	selected?: boolean;
	onToggle?: () => void;
}

export const OptionalServiceCardMobile = (props: Props): JSX.Element => {
	const classes = useStyles();
	const { optionalService, paxTotals, selectable, selected, onToggle } = props;
	const { description } = optionalService;
	const { isHtml, content } = renderDescription(description);
	const textContent = content
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	const shouldCollapseDescription = textContent.length > 130 || isHtml;
	const summaryText = shouldCollapseDescription ? `${textContent.substring(0, 118)}...` : textContent;

	return (
		<Card
			sx={{
				borderRadius: '10px',
				border: selected ? '2px solid' : '1px solid rgba(49, 25, 75, 0.10)',
				borderColor: selected ? 'secondary.main' : undefined,
				boxShadow: selected ? '0 10px 26px rgba(237, 102, 2, 0.16)' : '0 5px 16px rgba(49, 25, 75, 0.08)',
				overflow: 'hidden',
			}}
		>
			<CardMedia className={classes.media} image={optionalService.imageUrl} title={optionalService.name} />
			<div>
				<CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
					<Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1} mb={1}>
						<Box minWidth={0}>
							<Typography component="h1" variant="h6" color="primary" className={classes.title}>
								{optionalService.name}
							</Typography>
							<Chip
								size="small"
								label="Opcional"
								sx={{
									mt: 0.75,
									height: 22,
									borderRadius: '999px',
									fontSize: 11,
									fontWeight: 800,
									bgcolor: 'rgba(49, 25, 75, 0.06)',
								}}
							/>
						</Box>
						{selectable && (
							<Box textAlign="right">
								<Switch color="secondary" checked={Boolean(selected)} onChange={onToggle} />
								<Typography
									variant="caption"
									color={selected ? 'secondary' : 'text.secondary'}
									fontWeight={800}
									display="block"
								>
									{selected ? 'Agregado' : 'Agregar'}
								</Typography>
							</Box>
						)}
					</Box>
					<Typography variant="body2" color="text.secondary" lineHeight={1.35}>
						{summaryText}
					</Typography>
					{shouldCollapseDescription && (
						<Accordion
							disableGutters
							elevation={0}
							sx={{
								mt: 0.75,
								backgroundColor: 'rgba(49, 25, 75, 0.035)',
								borderRadius: '8px',
								'&:before': { display: 'none' },
							}}
						>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon color="primary" />}
								sx={{ minHeight: 34, px: 1, '& .MuiAccordionSummary-content': { my: 0.75 } }}
							>
								<Typography variant="caption" color="primary" fontWeight={800}>
									Ver más info
								</Typography>
							</AccordionSummary>
							<AccordionDetails sx={{ px: 1, pt: 0, pb: 1 }}>
								<Box
									sx={{
										fontFamily: 'Montserrat, sans-serif',
										color: '#272727',
										fontSize: '0.82rem',
										lineHeight: 1.4,
										'& ul': {
											paddingLeft: '14px',
											marginTop: 0,
											marginBottom: 0,
										},
										'& li': {
											fontSize: '0.82rem',
											lineHeight: 1.4,
										},
									}}
								>
									{isHtml ? (
										<div dangerouslySetInnerHTML={{ __html: content }} />
									) : (
										<Typography style={{ whiteSpace: 'pre-line' }} component={'span'} variant={'body2'}>
											{content}
										</Typography>
									)}
								</Box>
							</AccordionDetails>
						</Accordion>
					)}
				</CardContent>

				<Box className={classes.prices} sx={{ px: 1.5, pb: 1.25 }}>
					{optionalService.adultPrice && (
						<div className={classes.priceRow}>
							<Typography component={'span'} variant={'body2'} className={classes.pricesText}>
								Precio por adulto:
							</Typography>
							<Typography component={'span'} variant={'body2'} className={classes.pricesText}>
								USD {separadorDeMiles(optionalService.adultPrice)}
							</Typography>
						</div>
					)}
					{paxTotals.child > 0 && optionalService.childPrice != undefined && (
						<div className={classes.priceRow}>
							<Typography component={'span'} variant={'body2'} className={classes.pricesText}>
								Precio por niño:
							</Typography>
							<Typography component={'span'} variant={'body2'} className={classes.pricesText}>
								USD {separadorDeMiles(optionalService.childPrice)}
							</Typography>
						</div>
					)}
					{paxTotals.infants > 0 && optionalService.infantPrice != undefined && (
						<div className={classes.priceRow}>
							<Typography component={'span'} variant={'body2'} className={classes.pricesText}>
								Precio por bebé:
							</Typography>
							<Typography component={'span'} variant={'body2'} className={classes.pricesText}>
								USD {separadorDeMiles(optionalService.infantPrice)}
							</Typography>
						</div>
					)}
				</Box>

				{optionalService?.url && (
					<Box display="flex" justifyContent="center" alignItems="center" pb={1.25}>
						<Link
							href={optionalService?.url}
							color="inherit"
							target="_blank"
							rel="noopener"
							variant="h5"
							className={classes.title}
						>
							<Button variant="outlined" size="small" startIcon={<AddCircleOutlineIcon />}>
								Ver detalles
							</Button>
						</Link>
					</Box>
				)}
			</div>
		</Card>
	);
};
